import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { THIS_APP } from "types";

export default function ProtectedRoute() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  /**
   * Signed in, but not for this tool.
   *
   * `apps` is computed by the API from the roles held, so this is membership
   * rather than a second copy of the access rule. Shown as a message instead of
   * a redirect: bouncing a valid session to /login looks like the password
   * failed, and they would sign back in and bounce again.
   *
   * The API enforces the same rule on every request behind this — an older
   * backend that does not send `apps` simply lets the page through, and the
   * server still refuses the data.
   */
  const apps = user?.apps;
  if (apps && !apps.includes(THIS_APP)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Alert severity="warning" sx={{ maxWidth: 520 }}>
          <AlertTitle>No access to the Outbound tool</AlertTitle>
          <Stack spacing={1.5}>
            <span>
              {user?.email} is signed in, but does not hold a role that opens
              this tool. Ask a super-admin to add the Outbound sales role.
            </span>
            <Box>
              <Button size="small" variant="outlined" onClick={logout}>
                Sign in as someone else
              </Button>
            </Box>
          </Stack>
        </Alert>
      </Box>
    );
  }

  return <Outlet />;
}
